// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  // Calendar and CalendarClock return with the hidden sections below.
  X, Search, Settings as SettingsIcon, User, Users, Building2, Plug,
  CreditCard, LogOut, Package, ShieldCheck, Bell, Server, KeyRound,
} from 'lucide-react';
import Profile from '../pages/Profile';
import CalendarSettings from '../pages/CalendarSettings';
import ClientTeamTab from './clients/ClientTeamTab';
import DepartmentsTab from './settings/DepartmentsTab';
import { agentsApi } from '../services/agentsApi';
import BuilderWorkspaceCard from './BuilderWorkspaceCard';
import SubscriptionCard from './settings/SubscriptionCard';
import { ConnectionRow } from './onboarding/ConnectedAppsSection';
import AppConnectHub from './onboarding/appConnect/AppConnectHub';
import { APPS_CONNECT_OPEN, AppsConnectOpenDetail } from './onboarding/appConnect/appConnectEvents';
import { getClientConnections } from '../services/api';
import RoutinesTab from './settings/RoutinesTab';
import EmployeePreferences from './settings/EmployeePreferences';
import EmployeeBoundary from './settings/EmployeeBoundary';
import EmployeeAppsPanel from './settings/EmployeeAppsPanel';
import { McpSettingsPanel } from './settings/McpSettingsPanel';
import { CapabilitiesPanel } from './settings/CapabilitiesPanel';
import { useParticipant } from '../hooks/useParticipant';
import { usePermissions } from '../hooks/usePermissions';
import { DepartmentActivityPanel, DepartmentAiPanel, DepartmentAppsPanel, DepartmentPeoplePanel, EmployeeAiPanel } from './settings/DepartmentSettingsPanels';
import { ChampionPlanPanel } from './settings/ChampionSettingsPanels';
import { BillingPanel } from './settings/BillingPanel';
import { AiRuntimePanel } from './settings/AiRuntimePanel';
// AI & models is parked for now.
// import AiProvidersSection from './settings/AiProvidersSection';
import {
  AccountPanel, ActivityAuditPanel, AiPerformancePanel, AppsPermissionsPanel, OrganisationPanel, PeopleDepartmentsPanel,
  PlanPanel, QuickWinControlsPanel, RolesAccessPanel, roleLabel,
} from './settings/OwnerSettingsPanels';

type SectionId =
  | 'general'
  | 'account'
  | 'notifications'
  | 'security'
  | 'apps'
  | 'ai-performance'
  | 'skills'
  | 'team'
  | 'departments'
  | 'roles'
  | 'organisation'
  | 'connectors'
  | 'mcp'
  | 'plugins'
  | 'skill-library'
  | 'plugin-library'
  | 'capabilities'
  | 'billing'
  | 'payment'
  | 'calendar'
  | 'routines'
  | 'people'
  | 'plan'
  | 'audit'
  | 'ai-providers'
  | 'ai-runtime';

type SectionGroup = 'settings' | 'customize' | 'org';

// `managed` sections need manage authority. `member` sections are the
// employee-facing six: they are the ONLY ones an employee is offered, so the
// modal never shows them a workspace control they cannot use.
const SECTIONS: Array<{
  id: SectionId; label: string; icon: React.ElementType; group: SectionGroup;
  managed?: boolean; member?: boolean;
}> = [
  // Settings — identity, account, and workspace config.
  { id: 'general', label: 'General', icon: SettingsIcon, group: 'settings' },
  { id: 'account', label: 'Account', icon: User, group: 'settings' },
  // The employee set (Platform UI Guide §7). Notifications is theirs to change;
  // Security & data and AI performance are the organisation's policy shown
  // read-only, because a boundary you cannot see is a boundary you will cross.
  { id: 'notifications', label: 'Notifications', icon: Bell, group: 'settings', member: true },
  // { id: 'security', label: 'Security & data', icon: ShieldCheck, group: 'settings', member: true },
  { id: 'mcp', label: 'MCP', icon: Server, group: 'settings', member: true },
  { id: 'apps', label: 'Connectors', icon: Plug, group: 'settings', member: true },
  { id: 'capabilities', label: 'Skills & plugins', icon: Package, group: 'settings', member: true },
  // { id: 'ai-performance', label: 'AI performance', icon: Cpu, group: 'settings', member: true },
  { id: 'billing', label: 'Billing', icon: CreditCard, group: 'settings' },
  // { id: 'calendar', label: 'Calendar', icon: Calendar, group: 'settings', member: true },
  // F1: routines are personal (one runs as its owner, through their own
  // connections), so they sit beside Calendar rather than under the org group.
  // { id: 'routines', label: 'Routines', icon: CalendarClock, group: 'settings', member: true },
  // Customize — what extends the workspace.
  { id: 'capabilities', label: 'Skills & plugins', icon: Package, group: 'customize' },
  { id: 'connectors', label: 'Connectors', icon: Plug, group: 'customize' },
  // Org — organisation-level management (grouped under the org's name).
  // `managed` sections exist only for people with manage authority (owner,
  // permission-holders, staff) — a participant's modal never lists them.
  { id: 'team', label: 'Team', icon: Users, group: 'org' },
  { id: 'departments', label: 'Departments', icon: Building2, group: 'org', managed: true },
  // { id: 'roles', label: 'Roles', icon: ShieldCheck, group: 'org', managed: true },
  // { id: 'organisation', label: 'Organisation', icon: Building2, group: 'org' },
];

// The Owner's settings (the Sept 2026 settings UI): one flat list, personal
// things first, then the company. Each row maps to a panel that already exists
// and asks for the grant it always asked for; nothing here is Admin or Builder
// configuration (no model providers, no controls, no release settings).
const OWNER_SECTIONS: Array<{
  id: SectionId; label: string; icon: React.ElementType; group: SectionGroup;
  managed?: boolean; member?: boolean;
}> = [
  // { id: 'general', label: 'General', icon: SettingsIcon, group: 'settings' },
  { id: 'account', label: 'Account', icon: User, group: 'settings' },
  // Personal. These lived only on the /settings page,
  // which is retired, so without them here an Owner would lose both.
  // { id: 'calendar', label: 'Calendar', icon: Calendar, group: 'settings' },
  // { id: 'routines', label: 'Routines', icon: CalendarClock, group: 'settings' },
  { id: 'notifications', label: 'Notifications', icon: Bell, group: 'settings' },
  // { id: 'security', label: 'Security & data', icon: ShieldCheck, group: 'settings' },
  // MCP, Connectors, People & departments and Billing are Owner sidebar pages now.
  // { id: 'mcp', label: 'MCP', icon: Server, group: 'settings' },
  // { id: 'connectors', label: 'Connectors', icon: Plug, group: 'settings' },
  // { id: 'ai-performance', label: 'AI performance', icon: Cpu, group: 'settings' },
  { id: 'capabilities', label: 'Skills & plugins', icon: Package, group: 'org' },
  // { id: 'ai-providers', label: 'AI & models', icon: Cpu, group: 'org' },
  // { id: 'skills', label: 'Quick Win controls', icon: Wrench, group: 'org' },
  // { id: 'people', label: 'People & departments', icon: Users, group: 'org' },
  // { id: 'roles', label: 'Roles & access', icon: ShieldCheck, group: 'org', managed: true },
  // { id: 'organisation', label: 'Organisation', icon: Building2, group: 'org' },
  // { id: 'plan', label: 'Plan & usage', icon: CreditCard, group: 'org' },
  // { id: 'payment', label: 'Billing', icon: CreditCard, group: 'org' },
  { id: 'ai-runtime', label: 'AI Runtime', icon: KeyRound, group: 'org' },
  // { id: 'audit', label: 'Activity & audit', icon: CalendarClock, group: 'org' },
];

// The Department Head's modal: the Owner's sections, read where the role only
// reads, plus their own tools. Owner-managed controls render disabled with the
// reason instead of disappearing.
const DEPARTMENT_SECTIONS: typeof OWNER_SECTIONS = [
  // { id: 'general', label: 'General', icon: SettingsIcon, group: 'settings' },
  { id: 'account', label: 'Account', icon: User, group: 'settings' },
  // Personal. These lived only on the /settings page,
  // which is retired, so without them here an Owner would lose both.
  // { id: 'calendar', label: 'Calendar', icon: Calendar, group: 'settings' },
  // { id: 'routines', label: 'Routines', icon: CalendarClock, group: 'settings' },
  { id: 'notifications', label: 'Notifications', icon: Bell, group: 'settings' },
  // { id: 'security', label: 'Security & data', icon: ShieldCheck, group: 'settings' },
  { id: 'mcp', label: 'MCP', icon: Server, group: 'settings' },
  { id: 'connectors', label: 'Connectors', icon: Plug, group: 'settings' },
  // { id: 'ai-performance', label: 'AI performance', icon: Cpu, group: 'settings' },
  { id: 'capabilities', label: 'Skills & plugins', icon: Package, group: 'org' },
  { id: 'people', label: 'People & departments', icon: Users, group: 'org' },
  // { id: 'roles', label: 'Roles & access', icon: ShieldCheck, group: 'org' },
  // { id: 'plan', label: 'Plan & usage', icon: CreditCard, group: 'org' },
  // { id: 'audit', label: 'Activity & audit', icon: CalendarClock, group: 'org' },
];

// The AI Champion runs the program on the Owner's settings, minus the two
// controls that belong to the Owner alone: model providers and Quick Win policy.
const CHAMPION_HIDDEN = new Set<SectionId>(['ai-providers', 'skills', 'payment', 'ai-runtime']);

const ALL_SECTIONS = [...SECTIONS, ...OWNER_SECTIONS, ...DEPARTMENT_SECTIONS];

interface WorkspaceSettingsModalProps {
  open: boolean;
  section?: SectionId | string;
  onClose: () => void;
  onSection?: (section: SectionId) => void;
  clientId: string;
  clientName: string;
  userName: string;
  userEmail: string;
  userRole?: string | null;
  profile?: any;
  onLogout?: () => void;
}

function avatarInitials(input: string): string {
  return String(input || 'U')
    .split(/[\s@.]+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'U';
}

function formatRole(role?: string | null): string {
  if (!role) return 'Workspace';
  return String(role)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export const WorkspaceSettingsModal: React.FC<WorkspaceSettingsModalProps> = ({
  open,
  section = 'general',
  onClose,
  onSection,
  clientId,
  clientName,
  userName,
  userEmail,
  userRole,
  profile,
  onLogout,
}) => {
  const { isParticipant, isDepartmentHead, role } = useParticipant();
  const { can } = usePermissions();
  // The AI Champion's settings are the Owner's: the same sections and panels,
  // each asking for the grant it always asked for.
  const isChampion = role === 'AI_CHAMPION';
  const isOwner = role === 'OWNER' || isChampion;
  // A department head's modal is the employee's set plus their tools, with
  // Connectors only where the connector grant exists. Same sections,
  // different permissions.
  const isMember = isParticipant || isDepartmentHead;
  const [query, setQuery] = useState('');
  const [peopleTab, setPeopleTab] = useState<'team' | 'departments'>('team');
  const [active, setActive] = useState<SectionId>('general');
  const [connections, setConnections] = useState<ConnectionRow[]>([]);
  const [, setConnectionsLoading] = useState(false);
  const [connectHubOpen, setConnectHubOpen] = useState(false);
  const [connectHubPrefill, setConnectHubPrefill] = useState<string | null>(null);
  const [connectHubTab, setConnectHubTab] = useState<'add' | 'connected'>('add');

  useEffect(() => {
    if (!open) return;
    let next: SectionId = 'general';
    if (ALL_SECTIONS.some((s) => s.id === section)) next = section as SectionId;
    if (section === 'plugins' || section === 'plugin-library') {
      next = 'capabilities';
    }
    if (section === 'skills' || section === 'skill-library') {
      next = 'capabilities';
    }
    if (next === 'people' && (role === 'OWNER' || isChampion)) next = 'account';
    if (next === 'general' && (isMember || isOwner)) next = 'account';
    setActive(next);
    setQuery('');
  }, [open, section, isMember, isOwner, isChampion, role]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  const loadConnections = useCallback(async () => {
    if (!clientId) return;
    setConnectionsLoading(true);
    try {
      const data = await getClientConnections(clientId);
      setConnections(data?.connections || []);
    } catch (error) {
      console.warn('[WorkspaceSettingsModal] connections fetch failed', error);
      setConnections([]);
    } finally {
      setConnectionsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (!open || active !== 'connectors') return;
    loadConnections();
  }, [open, active, loadConnections]);

  useEffect(() => {
    if (!open) return;
    const onConnectOpen = (event: Event) => {
      const detail = ((event as CustomEvent).detail || {}) as AppsConnectOpenDetail;
      setConnectHubPrefill(detail.provider || null);
      setConnectHubTab(detail.tab || 'add');
      setConnectHubOpen(true);
    };
    window.addEventListener(APPS_CONNECT_OPEN, onConnectOpen as EventListener);
    return () => window.removeEventListener(APPS_CONNECT_OPEN, onConnectOpen as EventListener);
  }, [open]);

  // Manage authority decides whether the managed org sections exist at all.
  const [canManage, setCanManage] = useState(false);
  useEffect(() => {
    if (!open || !clientId) return;
    (async () => {
      try {
        const { data } = await agentsApi.getWorkspace(clientId);
        setCanManage(!!data?.can_manage);
      } catch {
        setCanManage(false);
      }
    })();
  }, [open, clientId]);

  // An employee's modal is the guide's six sections and nothing else. Everyone
  // else keeps the workspace set, minus anything needing manage authority.
  const visibleSections = useMemo(() => {
    if (isParticipant) {
      return SECTIONS.filter((s) => s.member || s.id === 'account');
    }
    if (isDepartmentHead) {
      return DEPARTMENT_SECTIONS.filter((s) => {
        if (s.id === 'connectors') return can('aai_use_connectors');
        if (s.id === 'people' || s.id === 'plan' || s.id === 'audit') return can('aai_view_department_rollup');
        return true;
      });
    }
    // The Owner and the Champion reach People & departments from the sidebar
    // (/team), so the section is not listed here a second time.
    if (isChampion) {
      return OWNER_SECTIONS.filter((s) => s.id !== 'people' && (!s.managed || canManage) && !CHAMPION_HIDDEN.has(s.id));
    }
    if (isOwner) {
      return OWNER_SECTIONS.filter((s) => s.id !== 'people' && (!s.managed || canManage));
    }
    return SECTIONS.filter((s) => !s.member && (!s.managed || canManage));
  }, [canManage, isParticipant, isDepartmentHead, isOwner, isChampion, can]);

  const navItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? visibleSections.filter((s) => s.label.toLowerCase().includes(q)) : visibleSections;
  }, [query, visibleSections]);

  const goSection = (next: SectionId) => {
    setActive(next);
    onSection?.(next);
  };

  if (!open) return null;

  const activeLabel = visibleSections.find((s) => s.id === active)?.label
    || ALL_SECTIONS.find((s) => s.id === active)?.label || 'Settings';
  const profileImage = profile?.profile_image;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4 sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="aa-settings flex h-[min(820px,100%)] w-[min(1120px,100%)] overflow-hidden rounded-panel border border-line bg-white shadow-[0_24px_60px_rgba(15,23,42,0.24)]">
        <aside className="hidden w-[248px] shrink-0 overflow-y-auto border-r border-line bg-[#F7F7F5] sm:flex sm:flex-col">
          <div className="flex items-center gap-3 px-5 pt-5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-card bg-ink text-[12px] font-bold text-white">
              {avatarInitials(clientName || userName || userEmail)}
            </span>
            <div className="min-w-0">
              <div className="text-[15px] font-bold leading-tight text-ink">Settings</div>
              <div className="truncate text-[12px] text-ink-dim">{clientName || 'Your workspace'}</div>
            </div>
          </div>
          <div className="px-5 pb-2 pt-5">
            <div className="mb-1.5 font-sans text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-dim">Search settings</div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-soft" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search settings"
                className="h-10 w-full rounded-card border border-line-strong bg-white pl-9 pr-3 text-[13px] text-ink outline-none placeholder:text-ink-soft focus:border-gray-950 focus:ring-2 focus:ring-gray-950/10"
              />
            </div>
          </div>
          <div className="flex-1 px-3 pb-3">
            {(isOwner || isDepartmentHead
              ? [{ id: 'settings' as SectionGroup, label: '' }]
              : ([
                { id: 'settings', label: 'Settings' },
                { id: 'customize', label: 'Customize' },
                { id: 'org', label: clientName || 'Organisation' },
              ] as Array<{ id: SectionGroup; label: string }>)
            ).map((group) => {
              const items = isOwner || isDepartmentHead ? navItems : navItems.filter((s) => s.group === group.id);
              if (items.length === 0) return null;
              return (
                <div key={group.id} className="mb-1">
                  {group.label && (
                    <div className="truncate px-2 py-1.5 font-sans text-[10px] font-semibold uppercase tracking-[0.09em] text-ink-soft">
                      {group.label}
                    </div>
                  )}
                  <nav className="space-y-0.5">
                    {items.map((item) => {
                      const selected = active === item.id;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => goSection(item.id)}
                          className={`flex w-full items-center justify-between rounded-card px-3 py-2.5 text-left text-[13.5px] transition-colors ${
                            selected
                              ? 'bg-white font-semibold text-ink shadow-sm'
                              : 'text-ink-muted hover:bg-white/70 hover:text-ink'
                          }`}
                        >
                          <span className="truncate">{item.label}</span>
                          {selected && <span aria-hidden className="text-ink-soft">→</span>}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 border-t border-line px-5 py-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-card bg-ink text-[12px] font-bold text-white">
              {avatarInitials(userName || userEmail)}
            </span>
            <div className="min-w-0">
              <div className="truncate text-[14px] font-bold leading-tight text-ink">{userName || 'You'}</div>
              <div className="truncate text-[12px] text-ink-dim">{role ? roleLabel(role) : formatRole(userRole)}</div>
            </div>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
          <header className="flex shrink-0 items-start justify-between gap-4 px-5 pb-4 pt-6 sm:px-8">
            <div className="min-w-0">
              <div className="font-sans text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-dim">{role ? roleLabel(role) : formatRole(userRole)}</div>
              <h2 className="mt-1.5 text-[26px] font-bold leading-tight tracking-[-0.015em] text-ink sm:text-[30px]">{activeLabel}</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line text-ink-dim transition-colors hover:bg-canvas-inset hover:text-ink"
              aria-label="Close settings"
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          <div className="border-b border-line px-4 py-3 sm:hidden">
            <select
              value={active}
              onChange={(event) => goSection(event.target.value as SectionId)}
              className="h-9 w-full rounded-lg border border-line bg-white px-3 text-[13px] font-medium text-ink outline-none focus:border-ink focus:ring-2 focus:ring-ink/15"
            >
              {visibleSections.map((item) => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 bg-white p-5 sm:p-6">
            {/* An employee's General is their own preferences, not the
                workspace's. The workspace panel below is for everyone else. */}
            {active === 'general' && (isMember || isOwner) && <EmployeePreferences tab="general" />}
            {active === 'notifications' && <EmployeePreferences tab="notifications" />}
            {active === 'security' && <EmployeeBoundary panel="security" />}
            {active === 'ai-performance' && isOwner && <AiPerformancePanel />}
            {active === 'ai-performance' && isDepartmentHead && <DepartmentAiPanel />}
            {active === 'ai-performance' && isParticipant && <EmployeeAiPanel />}
            {active === 'ai-performance' && !isOwner && !isDepartmentHead && !isParticipant && <EmployeeBoundary panel="ai" />}
            {active === 'apps' && isParticipant && <EmployeeAppsPanel />}
            {active === 'apps' && !isParticipant && <EmployeeAppsPanel />}

            {active === 'general' && !isMember && !isOwner && (
              <div className="mx-auto max-w-[760px] space-y-5">
                <Card>
                  <div className="flex items-center gap-4">
                    {profileImage ? (
                      <img
                        src={profileImage.startsWith('http') ? profileImage : `${process.env.REACT_APP_API_URL}/${profileImage}`}
                        alt=""
                        className="h-12 w-12 rounded-card object-cover"
                      />
                    ) : (
                      <span className="flex h-12 w-12 items-center justify-center rounded-card bg-brand-500 text-sm font-semibold text-white">
                        {avatarInitials(userName || userEmail)}
                      </span>
                    )}
                    <div className="min-w-0">
                      <div className="truncate text-[16px] font-semibold text-ink">{clientName || 'Your workspace'}</div>
                      <div className="truncate text-[12.5px] text-ink-dim">{userEmail || 'Signed in'}</div>
                    </div>
                  </div>
                  {onLogout && (
                    <button
                      type="button"
                      onClick={onLogout}
                      className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-medium text-ink-muted transition-colors hover:border-line-strong hover:bg-canvas hover:text-ink whitespace-nowrap"
                    >
                      <LogOut className="h-3.5 w-3.5" />
                      Sign out
                    </button>
                  )}
                </Card>
                <BuilderWorkspaceCard />
              </div>
            )}

            {active === 'account' && (isOwner || isMember) && (
              <div className="space-y-8">
                <AccountPanel clientName={clientName || 'Your workspace'} />
                {/* Parked for now: Preferences (start page, density, language, time zone).
                <div className="mx-auto max-w-[760px] border-t border-line-soft pt-6 text-[15px] font-bold text-ink">Preferences</div>
                <EmployeePreferences tab="general" />
                */}
              </div>
            )}
            {active === 'account' && !isOwner && !isMember && (
              <div className="mx-auto max-w-[900px]">
                <Profile />
              </div>
            )}

            {active === 'skills' && isOwner && <QuickWinControlsPanel />}

            {active === 'team' && (
              <div className="mx-auto max-w-[900px]">
                {clientId ? (
                  <ClientTeamTab clientId={clientId} clientName={clientName || 'Your workspace'} />
                ) : (
                  <EmptyState icon={Users} title="No workspace selected" body="Sign in again to manage your team." />
                )}
              </div>
            )}

            {active === 'departments' && (
              <div className="mx-auto max-w-[900px]">
                {clientId && canManage ? (
                  <DepartmentsTab clientId={clientId} />
                ) : (
                  <EmptyState icon={Building2} title="Managers only" body="Departments are managed by workspace owners and managers." />
                )}
              </div>
            )}

            {/* One roles panel, not two. The matrix answers the same question
                for everyone who may read it, so an owner and a manager were
                being shown the same list in two different designs. */}
            {active === 'roles' && clientId && (isOwner || isDepartmentHead || canManage) && (
              <RolesAccessPanel clientId={clientId} />
            )}
            {active === 'roles' && !isOwner && !isDepartmentHead && !canManage && (
              <div className="mx-auto max-w-[900px]">
                <EmptyState icon={ShieldCheck} title="Managers only" body="The roles matrix is visible to workspace owners and managers." />
              </div>
            )}

            {active === 'organisation' && isOwner && <OrganisationPanel clientName={clientName || 'Your workspace'} />}
            {active === 'organisation' && !isOwner && (
              <div className="mx-auto max-w-[760px] space-y-5">
                <Card>
                  <div className="text-[13px] font-medium text-ink">Workspace</div>
                  <Field label="Organisation" value={clientName || 'Your workspace'} />
                  <Field label="Signed in" value={userEmail || '—'} />
                  <Field label="Role" value={role ? roleLabel(role) : formatRole(userRole)} />
                </Card>
              </div>
            )}

            {active === 'connectors' && isOwner && <AppsPermissionsPanel clientId={clientId} />}
            {active === 'connectors' && isDepartmentHead && <DepartmentAppsPanel />}
            {active === 'connectors' && !isOwner && !isDepartmentHead && <EmployeeAppsPanel />}

            {active === 'mcp' && <McpSettingsPanel />}

            {active === 'capabilities' && clientId && <CapabilitiesPanel clientId={clientId} />}
            {active === 'capabilities' && !clientId && (
              <div className="mx-auto max-w-[760px]">
                <EmptyState icon={Package} title="No workspace selected" body="Sign in again to manage skills and plugins." />
              </div>
            )}

            {active === 'payment' && <BillingPanel clientId={clientId} />}

            {active === 'ai-runtime' && isOwner && !isChampion && <AiRuntimePanel />}

            {active === 'billing' && (
              <div className="mx-auto max-w-[760px]">
                <SubscriptionCard />
              </div>
            )}

            {active === 'calendar' && (
              <div className="mx-auto max-w-[900px]">
                <CalendarSettings />
              </div>
            )}

            {active === 'routines' && (
              <div className="mx-auto max-w-[900px]">
                <RoutinesTab />
              </div>
            )}

            {active === 'people' && isOwner && <PeopleDepartmentsPanel />}
            {active === 'people' && isDepartmentHead && <DepartmentPeoplePanel />}
            {active === 'people' && !isOwner && !isDepartmentHead && (
              <div className="mx-auto max-w-[900px]">
                <div role="tablist" aria-label="People view" className="mb-4 flex gap-0.5">
                  {(['team', 'departments'] as const).map((id) => (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={peopleTab === id}
                      onClick={() => setPeopleTab(id)}
                      className={`rounded-lg px-3 py-2 text-[13px] font-semibold transition-colors ${
                        peopleTab === id ? 'bg-canvas-inset text-ink' : 'text-ink-dim hover:bg-canvas-inset hover:text-ink'
                      }`}
                    >
                      {id === 'team' ? 'People' : 'Departments'}
                    </button>
                  ))}
                </div>
                {!clientId && <EmptyState icon={Users} title="No workspace selected" body="Sign in again to manage your team." />}
                {clientId && peopleTab === 'team' && (
                  <ClientTeamTab clientId={clientId} clientName={clientName || 'Your workspace'} />
                )}
                {clientId && peopleTab === 'departments' && canManage && <DepartmentsTab clientId={clientId} />}
                {clientId && peopleTab === 'departments' && !canManage && (
                  <EmptyState icon={Building2} title="Managers only" body="Departments are managed by workspace owners and managers." />
                )}
              </div>
            )}

            {active === 'plan' && clientId && isChampion && <ChampionPlanPanel clientId={clientId} />}
            {active === 'plan' && clientId && !isChampion && <PlanPanel clientId={clientId} />}

            {/* {active === 'ai-providers' && (
              <AiProvidersSection clientName={clientName || 'Your workspace'} roleLabel={role ? roleLabel(role) : formatRole(userRole)} />
            )} */}

            {active === 'audit' && clientId && isDepartmentHead && <DepartmentActivityPanel clientName={clientName || 'Your workspace'} />}
            {active === 'audit' && clientId && !isDepartmentHead && <ActivityAuditPanel clientId={clientId} clientName={clientName || 'Your workspace'} />}
          </div>
        </div>
      </div>

      {clientId && (
        <AppConnectHub
          open={connectHubOpen}
          onClose={() => {
            setConnectHubOpen(false);
            setConnectHubPrefill(null);
          }}
          clientId={clientId}
          connections={connections}
          suggestedSlugs={[]}
          onConnected={loadConnections}
          initialProvider={connectHubPrefill}
          initialTab={connectHubTab}
        />
      )}
    </div>
  );
};

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-card border border-line bg-white p-5 shadow-sm">
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-line-soft py-2.5 last:border-0">
      <span className="text-[12.5px] text-ink-dim">{label}</span>
      <span className="text-[13px] font-medium text-ink">{value}</span>
    </div>
  );
}

function EmptyState({ icon: Icon, title, body }: { icon: React.ElementType; title: string; body: string }) {
  return (
    <div className="rounded-card border border-line bg-white px-5 py-12 text-center">
      <Icon className="mx-auto mb-3 h-8 w-8 text-ink-soft" />
      <div className="text-sm font-semibold text-ink">{title}</div>
      <div className="mt-1 text-sm text-ink-dim">{body}</div>
    </div>
  );
}

export default WorkspaceSettingsModal;
